// @ts-nocheck
import { capitalize, isEmpty, uniq } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  Checkbox,
  IconTooltip,
  InputSearch,
  Selector,
  SelectorOptionPropsInterface,
  useDidUpdate
} from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import {
  reportingSetCampaignItemDetailsAnalyticalView,
  reportingSetCampaignItemDetailsCampaignStatus,
  reportingSetCampaignItemDetailsEventFamilies,
  reportingSetCampaignItemDetailsOnlyTopCampaigns,
  reportingSetCampaignItemDetailsSearchText
} from '@/store/actions/reporting.actions';
import {
  getCampaignItemDetailsCampaignStatus,
  getCampaignItemDetailsEventFamilies,
  getCampaignItemDetailsSearchText,
  isCampaignItemDetailsAnalyticalViewEnabled,
  isCampaignItemDetailsOnlyTopCampaignsEnabled
} from '@/store/selectors/reporting.selectors';
import { getCampaigns } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { useSummaryContext } from '@/ts-contexts/reporting/Summary/SummaryContext';
import { SummaryCampaignItemDetailsFiltersStyle } from './summaryCampaignItemDetailsFilters.style';

import type { CampaignProgressAndGoals } from 'sonata-static-storage/lib/common/common.types';

export function SummaryCampaignItemDetailsFilters(): React.ReactElement {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const analyticalViewIsEnabled = useSelector(isCampaignItemDetailsAnalyticalViewEnabled);
  const campaignStatus = useSelector(getCampaignItemDetailsCampaignStatus);
  const eventFamilies = useSelector(getCampaignItemDetailsEventFamilies);
  const searchText = useSelector(getCampaignItemDetailsSearchText);
  const selectedCampaignIds = useSelector(getCampaigns);
  const onlyTopCampaignsIsEnabled = useSelector(isCampaignItemDetailsOnlyTopCampaignsEnabled);
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);

  const { getSummaryDetailStats, summaryDetailStatsReady, summaryItemsKeys, summaryStatsError, summaryStatsReady } =
    useSummaryContext();
  const detailStats = getSummaryDetailStats();

  const reportingCampaignDetailsAnalyticsFilterPermission = checkUserResourcePermissions(
    'reportingCampaignItemDetailsAnalytics',
    user,
    userRoles
  );

  const [{ campaignStatusSelectorDisabled }, setState] = useState({
    campaignStatusSelectorDisabled: true
  });

  const validOverviewItemsKeys = useMemo(() => {
    return summaryItemsKeys.filter(itemKey => !['doohDemographyGender'].includes(itemKey));
  }, [summaryItemsKeys]);

  useEffect(() => {
    if (!eventFamilies && !isEmpty(summaryItemsKeys)) {
      dispatch(reportingSetCampaignItemDetailsEventFamilies(validOverviewItemsKeys));
    }
  }, [summaryItemsKeys, eventFamilies]);

  const campaignsData = useMemo(() => {
    if (summaryDetailStatsReady) {
      return detailStats
        .filter(({ id }) => selectedCampaignIds.includes(id))
        .reduce(
          (acc, detailEntryStats) => ({
            ...acc,
            [detailEntryStats.id!]: detailEntryStats
          }),
          {} as Record<string, CampaignProgressAndGoals>
        );
    }
    return {};
  }, [summaryDetailStatsReady, detailStats]);

  const campaignStatuses = useMemo(() => {
    return uniq(
      Object.values(campaignsData).map(campaignData => {
        const campaignGlobalStatus = campaignData.progress.find(campaignStatusEntry => {
          return campaignStatusEntry.timeFrame === 'overall' && campaignStatusEntry.type === 'budget';
        });
        return campaignGlobalStatus ? campaignGlobalStatus.progressGoal.status : 'noData';
      })
    );
  }, [campaignsData]);

  useDidUpdate(() => {
    setState(prevState => ({
      ...prevState,
      campaignStatusSelectorDisabled: campaignStatuses.length < 2
    }));
  }, [campaignStatuses]);

  const eventFamiliesOptions = useMemo(
    () =>
      validOverviewItemsKeys.map(overviewItemsKey => ({
        id: overviewItemsKey,
        label: formatMessage({
          defaultMessage: capitalize(overviewItemsKey),
          id: `reporting.metrics.${overviewItemsKey}`
        })
      })),
    []
  );

  const defaultEventFamiliesOptions = useMemo(() => {
    if (eventFamiliesOptions.length && !eventFamilies) {
      return eventFamiliesOptions;
    }
    return eventFamiliesOptions.filter(option => eventFamilies?.includes(option.id));
  }, [eventFamiliesOptions, eventFamilies]);

  const onEventFamiliesOptionChanged = useCallback(
    (newOptions: SelectorOptionPropsInterface[]) => {
      dispatch(reportingSetCampaignItemDetailsEventFamilies(newOptions.map(option => option.id)));
    },
    [eventFamiliesOptions]
  );

  const campaignStatusOptions = useMemo(() => {
    return campaignStatuses.map(status => ({
      id: status,
      label: formatMessage({
        defaultMessage: capitalize(status),
        id: `reporting.campaignItemDetailsFilters.selectors.campaignStatus.statuses.${status}`
      })
    }));
  }, [campaignStatuses]);

  const defaultCampaignStatusOptions = useMemo(() => {
    if (campaignStatusOptions.length && !campaignStatus) {
      return campaignStatusOptions;
    }
    return campaignStatusOptions.filter(campaignStatusOption => campaignStatus?.includes(campaignStatusOption.id));
  }, [campaignStatusOptions, campaignStatus]);

  const onCampaignStatusChanged = useCallback(
    (newOptions: SelectorOptionPropsInterface[]) => {
      dispatch(reportingSetCampaignItemDetailsCampaignStatus(newOptions.map(option => option.id)));
    },
    [eventFamiliesOptions]
  );

  const textChanged = useCallback(newSearchText => {
    dispatch(reportingSetCampaignItemDetailsSearchText(newSearchText));
  }, []);

  const onlyTopCampaignsToggled = useCallback(() => {
    dispatch(reportingSetCampaignItemDetailsOnlyTopCampaigns(!onlyTopCampaignsIsEnabled));
  }, [onlyTopCampaignsIsEnabled]);

  const analyticalViewToggled = useCallback(() => {
    dispatch(reportingSetCampaignItemDetailsAnalyticalView(!analyticalViewIsEnabled));
  }, [analyticalViewIsEnabled]);

  return (
    <SummaryCampaignItemDetailsFiltersStyle className="reportingCampaignItemDetailsFilters">
      <div className="reportingCampaignItemDetailsFilters__filtersSection">
        <Selector
          className="reportingCampaignItemDetailsFilters__eventFamiliesSelector"
          defaultValue={defaultEventFamiliesOptions}
          id="reportingCampaignItemDetailsFilterShowEventsSelector"
          label={formatMessage({
            defaultMessage: 'Events to show',
            id: 'reporting.campaignItemDetailsFilters.selectors.eventsShow.label'
          })}
          onChange={onEventFamiliesOptionChanged}
          options={eventFamiliesOptions}
          placeholder={formatMessage({
            defaultMessage: 'Select events...',
            id: 'reporting.campaignItemDetailsFilters.selectors.eventsShow.placeholder'
          })}
          type="multiple"
        />
        <Selector
          className="reportingCampaignItemDetailsFilters__campaignStatusSelector"
          id="reportingCampaignItemDetailsFilterStatusSelector"
          isDisabled={campaignStatusSelectorDisabled}
          label={formatMessage({
            defaultMessage: 'Select status...',
            id: 'reporting.campaignItemDetailsFilters.selectors.campaignStatus.label'
          })}
          onChange={onCampaignStatusChanged}
          options={campaignStatusOptions}
          placeholder={formatMessage({
            defaultMessage: 'Campaign status',
            id: 'reporting.campaignItemDetailsFilters.selectors.campaignStatus.placeholder'
          })}
          type="multiple"
          value={defaultCampaignStatusOptions}
        />
        <InputSearch
          className="reportingCampaignItemDetailsFilters__searchByText"
          clearable
          // @ts-ignore
          defaultValue={searchText}
          id="reportingCampaignItemDetailsFilterSearchTextInput"
          lens
          notFloatingPlaceholder
          onChange={textChanged}
          placeholder={formatMessage({
            defaultMessage: 'Search by campaign line nam',
            id: 'reporting.campaignItemDetailsFilters.searchTextPlaceholder'
          })}
          required
          shape="squareUI"
          type="text"
        />
      </div>
      <div className="reportingCampaignItemDetailsFilters__dataViewSettings">
        <Checkbox
          active={onlyTopCampaignsIsEnabled}
          classes="reportingCampaignItemDetailsFilters__onlyTopCampaignsSwitch"
          disabled={!summaryStatsReady || summaryStatsError}
          id="reportingCampaignItemDetailsFilterOnlyTopCampaignsSwitch"
          isSwitch
          labelText={formatMessage({
            defaultMessage: 'Only top campaign',
            id: 'reporting.campaignItemDetailsFilters.switches.onlyTopCampaigns.placeholder'
          })}
          onClickAction={onlyTopCampaignsToggled}
        />
        <div className="reportingCampaignItemDetailsFilters__onlyTopCampaignsTooltip">
          <IconTooltip
            effect="solid"
            event="mouseenter"
            icon="fa-regular fa-circle-info"
            iconColor="#FF5B29"
            place="top"
          >
            {formatMessage({
              defaultMessage: 'Only the best performing campaigns will be shown.',
              id: 'reporting.campaignItemDetailsFilters.switches.onlyTopCampaigns.tooltipText'
            })}
          </IconTooltip>
        </div>
        {reportingCampaignDetailsAnalyticsFilterPermission && (
          <Checkbox
            active={analyticalViewIsEnabled}
            classes="reportingCampaignItemDetailsFilters__analyticalViewSwitch"
            disabled={!summaryStatsReady || summaryStatsError}
            id="reportingCampaignItemDetailsFilterAnalyticalViewSwitch"
            isSwitch
            labelText={formatMessage({
              defaultMessage: 'Analytical view',
              id: 'reporting.campaignItemDetailsFilters.switches.analyticalView.placeholder'
            })}
            onClickAction={analyticalViewToggled}
          />
        )}
      </div>
    </SummaryCampaignItemDetailsFiltersStyle>
  );
}
